import moment from 'moment';
import { IFileWall, ILocation } from '../entities/general.entities';
import { TLang } from '../entities/ui.entities';
import { translate } from './lang.helper';

export const areSimilarStrings = (
  s1: string | null | undefined,
  s2: string | null | undefined,
) => {
  if (s1 === null || s1 === undefined || s2 === null || s2 === undefined)
    return false;

  const c1 = s1.split(' ').join('').toLowerCase(),
    c2 = s2.split(' ').join('').toLowerCase();

  return c1 === c2;
};

const levenshtein = (a: string, b: string): number => {
  const matrix = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) == a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1),
        );
      }
    }
  }

  return matrix[b.length][a.length];
};

export const findMostSimilar = (
  target: string | null | undefined,
  words: string[],
): string | null => {
  if (!target) {
    return null;
  }
  let minDistance = Infinity;
  let mostSimilar = '';

  words.forEach(word => {
    const distance = levenshtein(target, word);
    if (distance < minDistance) {
      minDistance = distance;
      mostSimilar = word;
    }
  });

  return mostSimilar;
};

export const compareString = (
  s1: string | undefined | null,
  s2: string | undefined | null,
) => {
  return (s1 ?? '').localeCompare(s2 ?? '', 'en', {
    numeric: true,
  });
};

export const randomKey = (): string => {
  const key = `${Math.random().toString(36).substr(2, 9)}`;
  return key;
};

export const calcPeriodUnit = (period: number) => {
  const m = 3600 * 24 * 30;
  const w = 3600 * 24 * 7;
  const d = 3600 * 24;
  if (period % m === 0) {
    return { unit: 'month', value: period / m };
  }
  if (period % w === 0) {
    return { unit: 'week', value: period / w };
  }
  if (period % d === 0) {
    return { unit: 'day', value: period / d };
  }
  return { unit: 'hour', value: period / 3600 };
};

export const periodToStr = (period: number) => {
  const { unit, value } = calcPeriodUnit(period);
  return `${value} ${unit}${value > 1 ? 's' : ''}`;
};

export const calcDiffDays = (
  fromTime: number,
  toTime: number = moment().toDate().getTime(),
) => moment(toMillisecond(toTime)).diff(toMillisecond(fromTime), 'days');

export const invertColor = (hex: string) => {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('');
  }
  const r = 255 - parseInt(hex.substring(0, 2), 16);
  const g = 255 - parseInt(hex.substring(2, 4), 16);
  const b = 255 - parseInt(hex.substring(4, 6), 16);

  return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
};

export const dozToPcs = (dozens: number | string) =>
  Math.round(12 * Number(dozens) * 100) / 100;

export const pcsToDoz = (pcs: number | string) =>
  Math.round((100 * Number(pcs)) / 12) / 100;

export const divideEqually = (t: number, d: number, i: number) =>
  Math.floor(t / d) + (i < t % d ? 1 : 0);

export const getAverageSize = (min: number, max: number | null | undefined) =>
  max === null || max === undefined ? min : Math.round((min + max) / 2);

export const generateWeeklyRanges = () => {
  const weeks = [];
  const startDate = moment();
  for (let i = 0; i < 52; i++) {
    const startOfWeek = startDate.clone().startOf('isoWeek');
    const endOfWeek = startDate.clone().endOf('isoWeek');

    weeks.push({
      start_date: startOfWeek.toDate().getTime(),
      finish_date: endOfWeek.toDate().getTime(),
      day_label: `${startOfWeek.format('DD.MM.YYYY')} - ${endOfWeek.format(
        'DD.MM.YYYY',
      )}`,
      week_label: `Week ${i + 1}`,
    });
    startDate.add(1, 'weeks');
  }
  return weeks;
};

export const generateMonthlyRanges = () => {
  const months = [];
  const startDate = moment();
  for (let i = 0; i < 12; i++) {
    const startOfMonth = startDate.clone().startOf('month');
    const endOfMonth = startDate.clone().endOf('month');

    months.push({
      start_date: startOfMonth.toDate().getTime(),
      finish_date: endOfMonth.toDate().getTime(),
      month_label: startOfMonth.format('MMMM'),
    });
    startDate.add(1, 'month');
  }
  return months;
};

export const directionChar = (s: string | null | undefined, ec: string = '') =>
  s === 'N' ? '↑' : s === 'S' ? '↓' : s === 'E' ? '→' : s === 'W' ? '←' : ec;

export const getBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

const getMediaTypeFromExtension = (ext: string) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
  const pdfExtensions = ['pdf'];
  if (imageExtensions.includes(ext.toLowerCase())) {
    return 'image';
  }
  if (pdfExtensions.includes(ext.toLowerCase())) {
    return 'pdf';
  }
  return undefined;
};

export const parseImageUrlToFileItem = (url: string): IFileWall | null => {
  try {
    const s = url.split('/');
    const name = s[s.length - 1];
    const type = name.split('.')[1] ?? 'Unknown';
    const uid = 'parseImageUrlToFileItem-' + Date.now() + Math.random();
    const media_type = getMediaTypeFromExtension(type) ?? 'image';
    return { uid, type, name, size: 1, thumbUrl: url, media_type };
  } catch (error) {
    console.log('url = ', url);
    return null;
  }
};

export const isColorFormat = (color: string) =>
  color && /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(color);

export const isEmailFormat = (s: string) =>
  !!s.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);

export const toMillisecond = (
  date: string | number | undefined | null,
): number => {
  if (date) {
    if (date.toString().length <= 10) {
      const millisecond = (Number(date) * 1000).toFixed();
      return Number(millisecond);
    } else {
      return Number(date);
    }
  }
  return moment().toDate().getTime();
};

export const defaultDateFormat = (
  date: string | number | null | undefined,
  eChar: string = '',
) => {
  if (date) {
    let ms = date;
    if (date.toString().length === 10) ms = (Number(date) * 1000).toFixed();
    return moment(Number(ms)).format('DD.MM.YYYY');
  } else {
    return eChar ?? '';
  }
};

export const toSecond = (date: string | number | null | undefined): number => {
  if (date) {
    if (date.toString().length <= 10) {
      return Number(date);
    } else {
      return Math.round(Number(date) / 1000);
    }
  }
  return Math.round(moment().toDate().getTime() / 1000);
};

export const toCapitalCase = (str: string) =>
  str
    .toLowerCase()
    .replace(/(?:^|\s|[_-])\w/g, match => match.toUpperCase())
    .replace(/[_-]/g, ' ');

export const getCenterOfPoints = (points: ILocation[]) => {
  if (points.length === 1) {
    return points[0];
  }
  const latPts = points.map(p => p.lat);
  const lngPts = points.map(p => p.lng);
  const minLat = Math.min(...latPts);
  const maxLat = Math.max(...latPts);
  const minLng = Math.min(...lngPts);
  const maxLng = Math.max(...lngPts);
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
};

export const amountDays = (
  time: number | undefined | null,
  lang: TLang | undefined = 'en',
  withMonths = false,
): string => {
  if (time === null || time === undefined) {
    return `0 ${translate(lang, 'days')}`;
  }
  if (Number(time) === 1) {
    return `1 ${translate(lang, 'day')}`;
  }
  if (withMonths) {
    const months = Math.floor(Number(time) / 30);
    if (months >= 1) {
      return `${time} ${translate(lang, 'days')} (${months} ${translate(lang, months === 1 ? 'month' : 'months')})`;
    }
  }
  return `${time} ${translate(lang, 'days')}`;
};

export const isNullOrUndefined = (v: any) => v === null || v === undefined;

export const numToStr = (v: number | null | undefined) =>
  v === undefined || v === null ? '' : v.toString();

export const areSameFloats = (
  a: number | null | undefined,
  b: number | null | undefined,
) => {
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }
  return Math.abs(a - b) < 0.001;
};

type TParamUnit = 'METRIC' | 'IMPERIAL' | null | undefined;
type TParamSpecie =
  | 'weight_kg'
  | 'weight_t'
  | 'size_mm'
  | 'distance_m'
  | 'distance_km'
  | 'area_hectare'
  | 'area_sqm'
  | 'volume_m3'
  | 'kg_per_m'
  | 'spat_mer_m'
  | 'droppers'
  | 'dz'
  | 'dozens'
  | 'pcs'
  | 'pieces'
  | '%'
  | undefined
  | null;

export const getUnitOfSpecie = (
  unitType: TParamUnit,
  unitSpecie: TParamSpecie,
) => {
  const unitMap: Record<string, { metric: string; imperial: string }> = {
    weight_kg: { metric: 'kg', imperial: 'lbs' },
    weight_t: { metric: 't', imperial: 'tons' },
    size_mm: { metric: 'mm', imperial: 'inches' },
    distance_m: { metric: 'm', imperial: 'feet' },
    distance_km: { metric: 'km', imperial: 'miles' },
    area_hectare: { metric: 'ha', imperial: 'acres' },
    area_sqm: { metric: 'm²', imperial: 'sqft' },
    volume_m3: { metric: 'm³', imperial: 'ft³' },
    kg_per_m: { metric: 'kg/m', imperial: 'lbs/ft' },
    spat_mer_m: { metric: 'spat/m', imperial: 'spat/ft' },
  };

  if (!unitSpecie) return '';

  const mapping = unitMap[unitSpecie];
  if (mapping) {
    return unitType === 'IMPERIAL' ? mapping.imperial : mapping.metric;
  }

  return unitSpecie;
};

export const displayValueUnit = (
  val: number | null | undefined,
  unitType: TParamUnit = undefined,
  unitSpecie: TParamSpecie = undefined,
  emptyCharacter: string = '',
) => {
  if (val === null || val === undefined) {
    return emptyCharacter;
  }
  if (unitSpecie === undefined || unitSpecie === null) {
    return `${Math.round(val * 100) / 100}`;
  }
  const unit = getUnitOfSpecie(unitType, unitSpecie);
  return `${Math.round(val * 1000) / 1000} ${unit}`;
};
